import { forwardRef } from 'react';

import { Button as BaseButton } from '@base-ui/react/button';

import { IconButton } from '../Button';
import { SpinnerGlyph } from '../Spinner/SpinnerGlyph';
import { cn } from '../utils';
import CloseIcon from './close.svg?react';
import {
  tagContentRecipe,
  tagIconRecipe,
  tagLabelRecipe,
  tagRecipe,
  tagRemoveIconRecipe,
  tagRemoveRecipe,
} from './Tag.recipe';

import type { TagColor, TagProps, TagStatus } from './types';

/** The colour each status owns; `generating` is neutral and also busy, through the existing `loading` behaviour. */
const STATUS_TONE: Record<TagStatus, { color: TagColor; loading?: true }> = {
  draft: { color: 'neutral' },
  upcoming: { color: 'primary' },
  live: { color: 'success' },
  ended: { color: 'neutral' },
  canceled: { color: 'neutral' },
  generating: { color: 'neutral', loading: true },
  ready: { color: 'success' },
  failed: { color: 'destructive' },
};

/**
 * Design System 2.0 tag.
 *
 * Ported from the reworked Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 597:43779).
 * Props mirror the Figma axes and boolean properties, so "Accent Sm removable" reads straight
 * across: `color`, `size`, `onDelete`. The two boolean properties in the file — `Icon` and
 * `isRemovable`, which toggle the visibility of the `Waveform` and `Button` layers — are the
 * `icon` and `onDelete` props: passing the slot IS switching the layer on.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer in
 * `packages/design-system/src/styles/design-system.css`, so the same markup is correct in both themes.
 *
 * A tag is DATA by default: no `onClick`, no `onDelete`, no tab stop, and no interactive element in
 * the tree at all. Both controls are opt-in, and both are opted into by passing the callback rather
 * than a boolean, so neither can exist without somewhere to report to.
 *
 * Distinct from `Badge` in this same layer, which is a NUMBER the system produces and keeps up to
 * date: it holds digits and a plus sign, can never hold a word, and is never interactive. A word, a
 * status ("Draft", "Live") or anything clickable belongs here instead. Also distinct from a filter
 * chip, which toggles — the Figma set draws no selected cell, so this does not either.
 *
 * Corrected when `Badge` was ported: this note used to point at `Badge` in `common/components` and
 * call it a count indicator. That was wrong in both halves — the legacy component is a styled
 * uppercase text span, so it is a Tag by behaviour despite its name, which is exactly the
 * `…Badge`-named-but-Tag-shaped confusion the design library's own docs page warns about.
 */
export const Tag = forwardRef<HTMLDivElement, TagProps>(
  (
    {
      children,
      className,
      color: colorProp,
      status,
      category,
      size,
      icon,
      loading: loadingProp,
      disabled,
      onClick,
      onDelete,
      deleteLabel,
      ...rest
    },
    ref
  ) => {
    const tone = status ? STATUS_TONE[status] : undefined;
    const color = tone ? tone.color : colorProp;
    const loading = loadingProp || tone?.loading;

    // The label and its icon, rendered identically whether or not they sit inside a button. A
    // variable rather than a nested component: a component defined in render would remount the
    // subtree on every parent render, and `react/no-unstable-nested-components` blocks it anyway.
    const content = (
      <>
        {loading ? (
          // The shared ring, aria-hidden. It sits in the icon slot, so it is the same 16px and takes
          // the same `--ds-tag-icon-fg` as any glyph would.
          <span className={tagIconRecipe()}>
            <SpinnerGlyph />
          </span>
        ) : (
          icon && <span className={tagIconRecipe()}>{icon}</span>
        )}
        <span className={tagLabelRecipe()}>{children}</span>
      </>
    );

    return (
      <div
        ref={ref}
        aria-disabled={disabled || undefined}
        className={cn(
          tagRecipe({
            // The recipe defaults `color` to neutral, so a category needs an explicit null or the
            // root would carry both `ds-tag-neutral` and `ds-tag-<hue>`.
            color: category ? null : color,
            category,
            size,
            interactive: Boolean(onClick || onDelete),
          }),
          className
        )}
        {...rest}
        // After the spread, and left out of the prop type, so a caller cannot un-busy a loading tag.
        aria-busy={loading || undefined}
      >
        {/* The root is NEVER a button, even when the whole tag is clickable. A deletable, clickable
            tag would otherwise nest one button inside another, which is invalid HTML and leaves the
            inner control unreachable for assistive technology. Making the label the control and the
            × its sibling keeps them two independent controls at every combination. */}
        {onClick ? (
          <BaseButton className={tagContentRecipe({ interactive: true })} disabled={disabled} onClick={onClick}>
            {content}
          </BaseButton>
        ) : (
          <span className={tagContentRecipe()}>{content}</span>
        )}

        {onDelete && (
          // The real `IconButton`, not a hand-rolled control. The file draws this as a
          // `Size=Tiny, Type=Tertiary, Fill=Default, iconOnly=Yes` Button instance, which reads
          // as `3xs` / `tertiary` / `default` in code. Going through the component is what gives
          // the × its hover and pressed states (`background/on-surface/hover` and `/active`)
          // rather than leaving it inert, and since #1180 its focus ring too. `tagRemoveRecipe`
          // adds only the 24px touch target and the file's 2px of leading space.
          //
          // No `size` argument varies by Tag size: the file draws this control 16x16 at BOTH.
          <IconButton
            // `IconButton` requires `aria-label: string`, and this IS a `string` here — no cast
            // needed. `TagDeleteProps` is a discriminated union, and since TS 4.6 destructuring
            // one into consts keeps the bindings linked, so the `onDelete &&` guard above narrows
            // `deleteLabel` along with it. An earlier version of this comment claimed the
            // opposite; it was wrong, and it cost a reviewer a round. Verified rather than
            // assumed: `((v: string) => v)(deleteLabel)` compiles here.
            aria-label={deleteLabel}
            appearance="tertiary"
            className={tagRemoveRecipe()}
            // A native `disabled` rather than `aria-disabled`: it takes the control out of the tab
            // order AND blocks activation by pointer and by keyboard, which is what a disabled tag
            // needs. Base UI mirrors it as `data-disabled` for the cursor rule.
            disabled={disabled}
            fill="default"
            size="3xs"
            onClick={onDelete}
          >
            <CloseIcon className={tagRemoveIconRecipe()} />
          </IconButton>
        )}
      </div>
    );
  }
);
